import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import { Button } from './button';
import { Checkbox } from './form';
import type { Selection } from '../lib/selection';

/** Checkbox for the header of a selectable list: all, some (indeterminate) or none (CO-0048). */
export function SelectAllCheckbox<K>({ selection, label }: { selection: Selection<K>; label: string }) {
  return (
    <Checkbox
      aria-label={label}
      checked={selection.allSelected ? true : selection.someSelected ? 'indeterminate' : false}
      onCheckedChange={() => selection.toggleAll()}
    />
  );
}

/** Row checkbox; shift+click selects the range from the last clicked row. */
export function SelectRowCheckbox<K>({ selection, rowKey, label }: { selection: Selection<K>; rowKey: K; label: string }) {
  return (
    <Checkbox
      aria-label={label}
      checked={selection.isSelected(rowKey)}
      onClick={(e) => {
        e.preventDefault();
        selection.toggle(rowKey, e.shiftKey);
      }}
    />
  );
}

/** Action bar that appears while rows are selected: count, the bulk actions and clear. */
export function BulkBar({
  count,
  label,
  clearLabel,
  onClear,
  children,
}: {
  count: number;
  label: string;
  clearLabel: string;
  onClear: () => void;
  children: ReactNode;
}) {
  if (count === 0) return null;
  return (
    <div
      role="toolbar"
      aria-label={label}
      className="sticky bottom-4 z-30 mx-auto flex w-fit max-w-full flex-wrap items-center gap-2 rounded-lg border border-line bg-inverse px-3 py-2 text-on-inverse shadow-lg"
    >
      <span className="px-1 text-sm font-bold tabular">{label}</span>
      {children}
      <Button size="icon" variant="ghost" className="text-on-inverse hover:bg-navy/40" aria-label={clearLabel} onClick={onClear}>
        <X />
      </Button>
    </div>
  );
}
